/**
 * Charts, in inline SVG, with no charting dependency.
 *
 * The rules these hold to, all of which this data invites breaking:
 *
 * * **No dual axis, ever.** Studies and disk are different scales; that is two
 *   charts, never two y-axes on one.
 * * **Status colours stay reserved.** up/down/degraded and issue severity use
 *   `--status-*` with a label beside them; they are never spent as "series 4".
 * * **A gap is a gap.** `null` means the probe failed or nothing was recorded.
 *   Lines break rather than joining across it, because a line drawn through a
 *   gap asserts a measurement nobody took, and zero would read as "instant"
 *   for latency and "empty" for studies — both the opposite of the truth.
 * * **Every chart offers its own numbers.** `ChartFrame` ships a table toggle,
 *   which is also what discharges the light-mode contrast relief the palette
 *   carries (see `tokens.css`).
 */

import { useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export interface Point {
  /** X value: an ISO timestamp. */
  at: string;
  /** Y value, or null for "not measured" — never 0 as a stand-in. */
  value: number | null;
}

export interface Series {
  key: string;
  label: string;
  points: Point[];
  /** 1-8, indexing the categorical tokens. Fixed per entity, never by rank. */
  slot?: number;
  /** Overrides the categorical slot — only for status, which owns its hues. */
  color?: string;
}

// Tight. A chart is a figure in a card, and generous padding on a 720-wide
// viewBox scales up with the card until the data is a thin band in a lot of
// nothing.
const PAD = { top: 16, right: 16, bottom: 26, left: 46 };

function seriesColor(series: Series, index: number): string {
  if (series.color) return series.color;
  // Fixed order, never cycled. Past eight, a ninth series folds into "other"
  // upstream rather than getting a generated hue here.
  const slot = series.slot ?? Math.min(index + 1, 8);
  return `var(--series-${slot})`;
}

function niceTicks(min: number, max: number, count = 3): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return [min || 0];
  }
  const span = max - min;
  const raw = span / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? magnitude;
  const ticks: number[] = [];
  for (let value = Math.ceil(min / step) * step; value <= max; value += step) {
    ticks.push(Number(value.toFixed(6)));
  }
  return ticks.length > 0 ? ticks : [min, max];
}

/**
 * Title, legend, hover layer and the table toggle every chart carries.
 *
 * `coverage` is not decoration: it is where a chart says how far back the
 * record goes, so an empty stretch reads as "not recorded" rather than as a
 * fleet sitting at zero.
 */
export function ChartFrame({
  title,
  subtitle,
  series,
  coverage,
  unavailable,
  children,
  table,
}: {
  title: string;
  subtitle?: ReactNode;
  series?: Series[];
  coverage?: ReactNode;
  unavailable?: string;
  children: ReactNode;
  table?: ReactNode;
}) {
  const [showTable, setShowTable] = useState(false);
  const headingId = useId();

  return (
    <section className="viz" aria-labelledby={headingId}>
      <header className="viz__head">
        <div>
          <h3 className="viz__title" id={headingId}>
            {title}
          </h3>
          {subtitle ? <p className="viz__subtitle">{subtitle}</p> : null}
        </div>
        {table ? (
          <button
            type="button"
            className="viz__toggle"
            onClick={() => setShowTable((value) => !value)}
            aria-pressed={showTable}
          >
            {showTable ? 'Show chart' : 'Show numbers'}
          </button>
        ) : null}
      </header>

      {/* A legend whenever there is more than one series — identity must never
          be carried by colour alone. */}
      {series && series.length > 1 ? (
        <ul className="viz__legend">
          {series.map((entry, index) => (
            <li key={entry.key}>
              <span
                className="viz__swatch"
                style={{ background: seriesColor(entry, index) }}
                aria-hidden="true"
              />
              {entry.label}
            </li>
          ))}
        </ul>
      ) : null}

      {unavailable ? (
        <p className="viz__empty">{unavailable}</p>
      ) : showTable && table ? (
        table
      ) : (
        children
      )}

      {coverage ? <p className="viz__coverage">{coverage}</p> : null}
    </section>
  );
}

/** Time labels that suit the span being shown, not a fixed format. */
function timeLabel(at: number, spanMs: number): string {
  const date = new Date(at);
  if (spanMs <= 2 * 86_400_000) {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  if (spanMs <= 400 * 86_400_000) {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
}

/**
 * A multi-series line chart with a time axis and a hover readout.
 *
 * The readout is not decoration. A chart of an operational measure that cannot
 * be read off exactly is a shape, and the reason to look at this one is to find
 * a number — so hovering names the bucket and every series' value in it,
 * including the ones that are null, which is the case a shape cannot show.
 *
 * Segments break at nulls rather than interpolating across them.
 */
export function LineChart({
  series,
  height = 168,
  format = (value: number) => String(Math.round(value)),
  label,
  fill = true,
}: {
  series: Series[];
  height?: number;
  format?: (value: number) => string;
  label: string;
  /** A soft gradient under a single series. Off for multi-series, where
   *  overlapping fills obscure each other rather than adding anything. */
  fill?: boolean;
}) {
  const width = 720;
  const gradientId = useId().replace(/:/g, '');
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverX, setHoverX] = useState(0);

  const model = useMemo(() => {
    const times = [
      ...new Set(
        series.flatMap((entry) =>
          entry.points.map((point) => Date.parse(point.at)).filter(Number.isFinite),
        ),
      ),
    ].sort((a, b) => a - b);
    const values = series
      .flatMap((entry) => entry.points.map((point) => point.value))
      .filter((value): value is number => value !== null && Number.isFinite(value));
    if (times.length === 0 || values.length === 0) return null;

    const minX = times[0]!;
    const maxX = times[times.length - 1]!;
    // Zero baseline throughout: every measure on this page is a count or a
    // duration, and a truncated axis turns a small change into a cliff.
    const maxY = Math.max(...values) * 1.08 || 1;

    const x = (at: number) =>
      maxX === minX
        ? PAD.left + (width - PAD.left - PAD.right) / 2
        : PAD.left + ((at - minX) / (maxX - minX)) * (width - PAD.left - PAD.right);
    const y = (value: number) =>
      height - PAD.bottom - (value / maxY) * (height - PAD.top - PAD.bottom);

    return { times, minX, maxX, maxY, x, y, ticks: niceTicks(0, maxY) };
  }, [series, height]);

  if (model === null) {
    return <p className="viz__empty">Nothing recorded in this window.</p>;
  }

  const { times, minX, maxX, x, y, ticks } = model;
  const span = maxX - minX;
  const singleSeries = series.length === 1;
  const sparse = times.length <= 12;

  // Four x labels at most: more turns the axis into a wall of dates.
  const xTickCount = Math.min(times.length, 4);
  const xTicks =
    xTickCount <= 1
      ? [times[0]!]
      : Array.from({ length: xTickCount }, (_, i) =>
          times[Math.round((i * (times.length - 1)) / (xTickCount - 1))]!,
        );

  const hoveredAt = hoverIndex === null ? null : times[hoverIndex] ?? null;

  return (
    <div className="viz__plot">
      {hoveredAt !== null ? (
        <div
          className="viz__tip"
          style={{
            // Flipped to the left of the cursor past the midpoint, so the
            // readout never runs off the right edge of the card.
            left: hoverX > 0.6 ? undefined : `${hoverX * 100}%`,
            right: hoverX > 0.6 ? `${(1 - hoverX) * 100}%` : undefined,
          }}
        >
          <div className="viz__tip-when">{timeLabel(hoveredAt, span)}</div>
          {series.map((entry, index) => {
            const point = entry.points.find((p) => Date.parse(p.at) === hoveredAt);
            return (
              <div className="viz__tip-row" key={entry.key}>
                <span
                  className="viz__swatch"
                  style={{ background: seriesColor(entry, index) }}
                  aria-hidden="true"
                />
                <span>{entry.label}</span>
                <span className="viz__tip-value">
                  {/* An absent or null point is "not measured", and says so.
                      This is the one thing the line itself cannot show. */}
                  {point === undefined || point.value === null ? '—' : format(point.value)}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      <svg
        className="viz__svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={label}
        preserveAspectRatio="xMidYMid meet"
        onMouseLeave={() => setHoverIndex(null)}
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientX - box.left) / box.width;
          setHoverX(Math.min(Math.max(ratio, 0), 1));
          const at = minX + ratio * span;
          let nearest = 0;
          for (let i = 1; i < times.length; i += 1) {
            if (Math.abs(times[i]! - at) < Math.abs(times[nearest]! - at)) nearest = i;
          }
          setHoverIndex(nearest);
        }}
      >
        {fill && singleSeries ? (
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={seriesColor(series[0]!, 0)} stopOpacity="0.28" />
              <stop offset="100%" stopColor={seriesColor(series[0]!, 0)} stopOpacity="0" />
            </linearGradient>
          </defs>
        ) : null}

        {ticks
          .filter(
            (tick, index, all) =>
              all.findIndex((other) => format(other) === format(tick)) === index,
          )
          .map((tick) => (
            <g key={tick}>
              <line
                className="viz__grid"
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
              />
              <text className="viz__axis" x={PAD.left - 8} y={y(tick) + 4} textAnchor="end">
                {format(tick)}
              </text>
            </g>
          ))}

        {/* A time axis. Without it every one of these charts was a shape with
            no when — the single most common thing to want from them. */}
        {xTicks.map((at, index) => (
          <text
            key={`${at}-${index}`}
            className="viz__axis-x"
            x={x(at)}
            y={height - 6}
            textAnchor={index === 0 ? 'start' : index === xTicks.length - 1 ? 'end' : 'middle'}
          >
            {timeLabel(at, span)}
          </text>
        ))}

        {series.map((entry, index) => {
          const runs: Point[][] = [];
          let run: Point[] = [];
          for (const point of entry.points) {
            if (point.value === null) {
              if (run.length > 0) runs.push(run);
              run = [];
            } else {
              run.push(point);
            }
          }
          if (run.length > 0) runs.push(run);

          return runs.map((segment, runIndex) => {
            const line = segment
              .map(
                (point, i) =>
                  `${i === 0 ? 'M' : 'L'} ${x(Date.parse(point.at))} ${y(point.value as number)}`,
              )
              .join(' ');
            const first = segment[0]!;
            const last = segment[segment.length - 1]!;
            return (
              <g key={`${entry.key}-${runIndex}`}>
                {fill && singleSeries ? (
                  <path
                    d={
                      `${line} L ${x(Date.parse(last.at))} ${height - PAD.bottom} ` +
                      `L ${x(Date.parse(first.at))} ${height - PAD.bottom} Z`
                    }
                    fill={`url(#${gradientId})`}
                    stroke="none"
                  />
                ) : null}
                <path className="viz__line" stroke={seriesColor(entry, index)} d={line} />
                {/* Markers on a sparse series. Two measurements joined by a
                    bare line read as a trend line through data that is not
                    there; the dots say "these are the readings, and there are
                    two of them". Dropped once the line is dense enough to
                    carry its own shape. */}
                {sparse
                  ? segment.map((point) => (
                      <circle
                        key={point.at}
                        className="viz__dot"
                        cx={x(Date.parse(point.at))}
                        cy={y(point.value as number)}
                        r={3.5}
                        fill={seriesColor(entry, index)}
                      />
                    ))
                  : null}
              </g>
            );
          });
        })}

        {hoveredAt !== null ? (
          <>
            <line
              className="viz__crosshair"
              x1={x(hoveredAt)}
              x2={x(hoveredAt)}
              y1={PAD.top}
              y2={height - PAD.bottom}
            />
            {series.map((entry, index) => {
              const point = entry.points.find((p) => Date.parse(p.at) === hoveredAt);
              if (point === undefined || point.value === null) return null;
              return (
                <circle
                  key={entry.key}
                  className="viz__dot"
                  cx={x(hoveredAt)}
                  cy={y(point.value)}
                  r={4}
                  fill={seriesColor(entry, index)}
                />
              );
            })}
          </>
        ) : null}
      </svg>
    </div>
  );
}

/**
 * States over time, stacked to 100%.
 *
 * Status palette, not the categorical one: these bands mean up, degraded, down
 * and unknown, and those meanings are exactly what `--status-*` exists for.
 * A 2px gap between segments keeps adjacent bands legible without a border.
 */
export function StateBars({
  buckets,
  height = 128,
  label,
}: {
  buckets: {
    at: string;
    up: number;
    degraded: number;
    down: number;
    unknown: number;
    samples: number;
  }[];
  height?: number;
  label: string;
}) {
  const width = 720;
  if (buckets.length === 0) {
    return <p className="viz__empty">Nothing recorded in this window.</p>;
  }
  const slot = (width - PAD.left - PAD.right) / buckets.length;
  // Capped: a window holding two buckets should not render as two slabs half
  // the chart wide, which reads as a proportion of something rather than as a
  // bar. The 2px shortfall is the gap between adjacent segments.
  const barWidth = Math.min(Math.max(slot - 2, 1), 56);
  // Centred in its slot rather than left-aligned. With a capped width and few
  // buckets, left-aligning leaves each bar hugging the edge of a wide empty
  // space, which reads as a broken layout rather than as a sparse one.
  const inset = (slot - barWidth) / 2;

  const bands = [
    { key: 'up', color: 'var(--status-good)' },
    { key: 'degraded', color: 'var(--status-warning)' },
    { key: 'down', color: 'var(--status-critical)' },
    { key: 'unknown', color: 'var(--status-neutral)' },
  ] as const;

  return (
    <svg
      className="viz__svg"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* A baseline. Stacked bands with nothing under them read as floating,
          and the eye needs somewhere to measure the heights from. */}
      <line
        className="viz__grid"
        x1={PAD.left}
        x2={width - PAD.right}
        y1={height - PAD.bottom}
        y2={height - PAD.bottom}
      />

      {buckets.map((bucket, index) => {
        const x = PAD.left + index * slot + inset;
        const usable = height - PAD.top - PAD.bottom;
        // A bucket with no samples is a gap in the record, not a bucket that
        // was 0% up. It is drawn as nothing at all.
        if (bucket.samples === 0) return null;
        // A real outage must be VISIBLE. 13 down samples in 1,483 is 0.9%,
        // which on an 86px bar is 0.7 of a pixel — an outage the chart hides.
        // Any non-zero band therefore gets a floor of 3px, and the segments are
        // then no longer strictly proportional. That trade is deliberate and is
        // why the percentage is printed above each bar and the exact counts are
        // one click away in "Show numbers": the picture answers "did anything
        // go wrong", the numbers answer "how much".
        const MIN_BAND = 3;
        const present = bands.filter((band) => bucket[band.key] > 0);
        const raw = present.map((band) => (bucket[band.key] / bucket.samples) * usable);
        const owed = raw.reduce(
          (total, value) => total + Math.max(MIN_BAND - value, 0),
          0,
        );
        const shrinkable = raw.reduce(
          (total, value) => total + Math.max(value - MIN_BAND, 0),
          0,
        );
        const availability = bucket.up / bucket.samples;

        let offset = 0;
        return (
          <g key={bucket.at}>
            {present.map((band, bandIndex) => {
              const value = raw[bandIndex]!;
              // Bands above the floor give up a proportional share of what the
              // floored ones borrowed, so the bar still totals its full height.
              const bandHeight =
                value < MIN_BAND
                  ? MIN_BAND
                  : shrinkable > 0
                    ? value - (value - MIN_BAND) * (owed / shrinkable)
                    : value;
              const y = PAD.top + offset;
              offset += bandHeight;
              return (
                <rect
                  key={band.key}
                  x={x}
                  y={y}
                  width={barWidth}
                  height={Math.max(bandHeight - 1, 1)}
                  // Rounded ends, and the 1px shortfall above, keep adjacent
                  // segments legible without a border — a border would add a
                  // colour the data does not have.
                  rx={2}
                  fill={band.color}
                />
              );
            })}

            {/* Direct label. Two bars that are both "almost entirely green"
                differ by an amount no bar can show; the number is the only
                thing that distinguishes 100% from 99.6%. */}
            {buckets.length <= 12 ? (
              <text
                className="viz__bar-value"
                x={x + barWidth / 2}
                y={PAD.top - 5}
                textAnchor="middle"
              >
                {(availability * 100).toFixed(availability === 1 ? 0 : 1)}%
              </text>
            ) : null}
            {/* Only the ends are labelled: a label under every bar becomes a
                wall of dates, and the ends are what fix the span. */}
            {index === 0 || index === buckets.length - 1 ? (
              <text
                className="viz__axis-x"
                x={x + barWidth / 2}
                y={height - 6}
                textAnchor={index === 0 ? 'start' : 'end'}
              >
                {new Date(bucket.at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Magnitude across a set of things, ranked.
 *
 * ONE hue, not eight. The centers here are not identities being told apart —
 * they are one measure ordered, and spending the categorical palette on a
 * ranking implies a difference in kind that is not there. Opacity carries the
 * value; the bar length carries it again, so the encoding survives greyscale.
 *
 * Horizontal because the labels are center slugs: vertical bars would turn them
 * into rotated text, which is slower to read than the chart is to scan.
 */
export function RankedBars({
  rows,
  format,
  label,
  tone,
}: {
  rows: { key: string; label: ReactNode; value: number | null }[];
  format: (value: number) => string;
  label: string;
  /** Per-row status colour, when the value carries a state rather than a size. */
  tone?: (value: number) => string;
}) {
  const known = rows.filter(
    (row): row is { key: string; label: string; value: number } => row.value !== null,
  );
  if (known.length === 0) {
    return <p className="viz__empty">Nothing recorded in this window.</p>;
  }
  const max = Math.max(...known.map((row) => row.value), 0.0001);

  return (
    <ul className="viz__ranked" aria-label={label}>
      {rows.map((row) => (
        <li key={row.key}>
          <span className="viz__ranked-label">{row.label}</span>
          <span className="viz__ranked-track">
            {row.value === null ? null : (
              <span
                className="viz__ranked-fill"
                style={{
                  width: `${Math.max((row.value / max) * 100, 1)}%`,
                  background: tone ? tone(row.value) : 'var(--series-1)',
                }}
              />
            )}
          </span>
          {/* The number is always present. A bar you cannot read a value off
              is a picture, and this one is meant to be acted on. */}
          <span className="viz__ranked-value">
            {row.value === null ? '—' : format(row.value)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** A tiny inline trend, for a table row. No axes, no labels — a shape only. */
export function Sparkline({
  points,
  label,
  width = 96,
  height = 24,
}: {
  points: (number | null)[];
  label: string;
  width?: number;
  height?: number;
}) {
  const values = points.filter((v): v is number => v !== null && Number.isFinite(v));
  if (values.length < 2) {
    return <span className="viz__spark-empty" title={label} aria-label={label} />;
  }
  const max = Math.max(...values, 1);
  const step = width / Math.max(points.length - 1, 1);

  const runs: string[] = [];
  let current: string[] = [];
  points.forEach((value, index) => {
    if (value === null) {
      if (current.length > 0) runs.push(current.join(' '));
      current = [];
      return;
    }
    const x = index * step;
    const y = height - (value / max) * (height - 2) - 1;
    current.push(`${current.length === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`);
  });
  if (current.length > 0) runs.push(current.join(' '));

  return (
    <svg
      className="viz__spark"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
    >
      {runs.map((d, index) => (
        <path key={index} d={d} className="viz__spark-line" />
      ))}
    </svg>
  );
}

/** A ratio against a limit. One hue, filled from the same ramp as its track. */
export function Meter({
  value,
  max,
  label,
  tone = 'accent',
}: {
  value: number;
  max: number;
  label: string;
  tone?: 'accent' | 'good' | 'warning' | 'critical';
}) {
  const share = max > 0 ? Math.min(Math.max(value / max, 0), 1) : 0;
  return (
    <div
      className="viz__meter"
      role="meter"
      aria-valuenow={Math.round(share * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className="viz__meter-fill"
        style={{
          width: `${share * 100}%`,
          background: tone === 'accent' ? 'var(--accent)' : `var(--status-${tone})`,
        }}
      />
    </div>
  );
}

// ── columns and heatmaps: counts per calendar bucket ─────────────────────────

const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The default bucket label.
 *
 * A bare `YYYY-MM-DD` is a calendar day (a Baghdad day, from the upload
 * reports), not an instant — so it is formatted in UTC, where `Date.parse`
 * put it, and never shifted into the browser's zone, which would print the
 * 28th as the 27th for anyone west of Greenwich.
 */
function defaultBucketLabel(at: string): string {
  const parsed = Date.parse(DAY_ONLY.test(at) ? `${at}T00:00:00Z` : at);
  if (!Number.isFinite(parsed)) return at;
  return new Date(parsed).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(DAY_ONLY.test(at) ? { timeZone: 'UTC' } : {}),
  });
}

/** Every bucket any series mentions, in time order. */
function bucketsOf(series: Series[]): string[] {
  const all = [...new Set(series.flatMap((entry) => entry.points.map((p) => p.at)))];
  const key = (at: string) => Date.parse(DAY_ONLY.test(at) ? `${at}T00:00:00Z` : at);
  return all.sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    return Number.isFinite(ka) && Number.isFinite(kb) ? ka - kb : a.localeCompare(b);
  });
}

/** `series × bucket → value`, with `undefined` for "this series has no point
 *  there" — which the chart treats exactly like null: not measured. */
function valueGrid(series: Series[], buckets: string[]): (number | null)[][] {
  return series.map((entry) => {
    const byAt = new Map(entry.points.map((p) => [p.at, p.value]));
    return buckets.map((at) => {
      const value = byAt.get(at);
      return value === undefined || value === null || !Number.isFinite(value) ? null : value;
    });
  });
}

function isPartial(partial: readonly string[] | ReadonlySet<string> | undefined, at: string) {
  if (!partial) return false;
  return 'has' in partial ? partial.has(at) : partial.includes(at);
}

/**
 * Counts per calendar bucket, as columns — stacked or side by side.
 *
 * Columns rather than a line because these are counts OF a bucket (studies
 * received on the 28th), not a level sampled at an instant; a line between
 * two days' totals asserts a value in between that does not exist.
 *
 * Three states a column must keep apart, because each means something
 * different to the person reading it:
 *
 * * **null → nothing drawn.** Not computed, not reachable, not yet backfilled.
 *   The hover readout says "—", the one place a gap can be named.
 * * **0 → a 2px tick on the baseline.** Measured, and nothing arrived. An
 *   idle center must look different from a center nobody has counted.
 * * **partial → faded.** Today (or this week, this month) is still counting;
 *   drawn at full strength it reads as a sudden drop at the right edge of
 *   every chart. The readout says so in words as well.
 *
 * `stacked` is for parts of one whole (modalities of a center's uploads);
 * `grouped` is for measures that must NOT be added — an edge node's received
 * and replicated counts are the same studies seen twice.
 */
export function ColumnChart({
  series,
  mode = 'stacked',
  partial,
  height = 168,
  format = (value: number) => formatCount(value),
  bucketLabel = defaultBucketLabel,
  label,
}: {
  series: Series[];
  mode?: 'stacked' | 'grouped';
  /** Buckets (`at` values) that are still accumulating. */
  partial?: readonly string[] | ReadonlySet<string>;
  height?: number;
  format?: (value: number) => string;
  bucketLabel?: (at: string) => string;
  label: string;
}) {
  const width = 720;
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverX, setHoverX] = useState(0);

  const model = useMemo(() => {
    const buckets = bucketsOf(series);
    const grid = valueGrid(series, buckets);
    const measured = grid.some((row) => row.some((value) => value !== null));
    if (buckets.length === 0 || !measured) return null;

    const totals = buckets.map((_, b) => {
      const values = grid.map((row) => row[b] ?? null).filter((v): v is number => v !== null);
      return values.length === 0 ? null : values.reduce((sum, v) => sum + Math.max(v, 0), 0);
    });
    const peak =
      mode === 'stacked'
        ? Math.max(...totals.map((t) => t ?? 0))
        : Math.max(...grid.flatMap((row) => row.map((v) => v ?? 0)));
    // Zero baseline, and a floor of 1 so an all-zero window still has an axis
    // to put its ticks on rather than dividing by nothing.
    const maxY = peak > 0 ? peak * 1.08 : 1;
    return { buckets, grid, totals, maxY, ticks: niceTicks(0, maxY) };
  }, [series, mode]);

  if (model === null) {
    return <p className="viz__empty">Nothing recorded in this window.</p>;
  }

  const { buckets, grid, totals, maxY, ticks } = model;
  const plotWidth = width - PAD.left - PAD.right;
  const slot = plotWidth / buckets.length;
  // Capped for the same reason as StateBars: two buckets should not render as
  // two slabs half the chart wide.
  const barWidth = Math.min(Math.max(slot * 0.72, 1), 56);
  const inset = (slot - barWidth) / 2;
  const baseline = height - PAD.bottom;
  const y = (value: number) => baseline - (value / maxY) * (height - PAD.top - PAD.bottom);
  const ZERO_TICK = 2;

  // At most six x labels, always including both ends: the ends fix the span.
  const labelCount = Math.min(buckets.length, 6);
  const labelled = new Set(
    labelCount <= 1
      ? [0]
      : Array.from({ length: labelCount }, (_, i) =>
          Math.round((i * (buckets.length - 1)) / (labelCount - 1)),
        ),
  );
  const showTotals = mode === 'stacked' && buckets.length <= 12;
  const hoveredAt = hoverIndex === null ? null : buckets[hoverIndex] ?? null;

  return (
    <div className="viz__plot">
      {hoveredAt !== null && hoverIndex !== null ? (
        <div
          className="viz__tip"
          style={{
            left: hoverX > 0.6 ? undefined : `${hoverX * 100}%`,
            right: hoverX > 0.6 ? `${(1 - hoverX) * 100}%` : undefined,
          }}
        >
          <div className="viz__tip-when">
            {bucketLabel(hoveredAt)}
            {isPartial(partial, hoveredAt) ? ' · partial, still counting' : ''}
          </div>
          {series.map((entry, index) => {
            const value = grid[index]?.[hoverIndex] ?? null;
            return (
              <div className="viz__tip-row" key={entry.key}>
                <span
                  className="viz__swatch"
                  style={{ background: seriesColor(entry, index) }}
                  aria-hidden="true"
                />
                <span>{entry.label}</span>
                <span className="viz__tip-value">{value === null ? '—' : format(value)}</span>
              </div>
            );
          })}
          {mode === 'stacked' && series.length > 1 ? (
            <div className="viz__tip-row viz__tip-total">
              <span>Total</span>
              <span className="viz__tip-value">
                {totals[hoverIndex] === null ? '—' : format(totals[hoverIndex] as number)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <svg
        className="viz__svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={label}
        preserveAspectRatio="xMidYMid meet"
        onMouseLeave={() => setHoverIndex(null)}
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const ratio = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
          setHoverX(ratio);
          const index = Math.floor((ratio * width - PAD.left) / slot);
          setHoverIndex(Math.min(Math.max(index, 0), buckets.length - 1));
        }}
      >
        {ticks
          .filter(
            (tick, index, all) =>
              all.findIndex((other) => format(other) === format(tick)) === index,
          )
          .map((tick) => (
            <g key={tick}>
              <line className="viz__grid" x1={PAD.left} x2={width - PAD.right} y1={y(tick)} y2={y(tick)} />
              <text className="viz__axis" x={PAD.left - 8} y={y(tick) + 4} textAnchor="end">
                {format(tick)}
              </text>
            </g>
          ))}

        {hoverIndex !== null ? (
          <rect
            className="viz__col-hover"
            x={PAD.left + hoverIndex * slot}
            y={PAD.top}
            width={slot}
            height={baseline - PAD.top}
          />
        ) : null}

        {buckets.map((at, b) => {
          const x = PAD.left + b * slot + inset;
          const faded = isPartial(partial, at);
          let marks: ReactNode;
          if (mode === 'stacked') {
            const total = totals[b] ?? null;
            if (total === null) {
              marks = null;
            } else if (total === 0) {
              marks = (
                <rect className="viz__col-zero" x={x} y={baseline - ZERO_TICK} width={barWidth} height={ZERO_TICK} />
              );
            } else {
              let top = baseline;
              marks = series.map((entry, s) => {
                const value = grid[s]?.[b] ?? null;
                if (value === null || value <= 0) return null;
                const segment = baseline - y(value);
                top -= segment;
                return (
                  <rect
                    key={entry.key}
                    className="viz__col"
                    x={x}
                    y={top}
                    // The 1px shortfall is the gap between stacked parts.
                    height={Math.max(segment - (series.length > 1 ? 1 : 0), 1)}
                    width={barWidth}
                    rx={2}
                    fill={seriesColor(entry, s)}
                  />
                );
              });
            }
          } else {
            const sub = barWidth / Math.max(series.length, 1);
            marks = series.map((entry, s) => {
              const value = grid[s]?.[b] ?? null;
              if (value === null) return null;
              const sx = x + s * sub;
              const w = Math.max(sub - 1, 1);
              return value <= 0 ? (
                <rect
                  key={entry.key}
                  className="viz__col-zero"
                  x={sx}
                  y={baseline - ZERO_TICK}
                  width={w}
                  height={ZERO_TICK}
                  fill={seriesColor(entry, s)}
                />
              ) : (
                <rect
                  key={entry.key}
                  className="viz__col"
                  x={sx}
                  y={y(value)}
                  width={w}
                  height={Math.max(baseline - y(value), 1)}
                  rx={2}
                  fill={seriesColor(entry, s)}
                />
              );
            });
          }
          return (
            <g key={at} className={faded ? 'viz__col-group viz__col--partial' : 'viz__col-group'}>
              {marks}
              {showTotals && totals[b] !== null ? (
                <text
                  className="viz__bar-value"
                  x={x + barWidth / 2}
                  y={y(totals[b] as number) - 5}
                  textAnchor="middle"
                >
                  {format(totals[b] as number)}
                </text>
              ) : null}
              {labelled.has(b) ? (
                <text
                  className="viz__axis-x"
                  x={x + barWidth / 2}
                  y={height - 6}
                  textAnchor={
                    buckets.length > 1 && b === 0
                      ? 'start'
                      : buckets.length > 1 && b === buckets.length - 1
                        ? 'end'
                        : 'middle'
                  }
                >
                  {bucketLabel(at)}
                </text>
              ) : null}
            </g>
          );
        })}

        <line className="viz__grid" x1={PAD.left} x2={width - PAD.right} y1={baseline} y2={baseline} />
      </svg>
    </div>
  );
}

function formatCount(value: number): string {
  return new Intl.NumberFormat(undefined).format(Math.round(value));
}

/**
 * The numbers behind a ColumnChart, for `ChartFrame`'s table toggle.
 *
 * Same buckets, same null-is-a-dash rule, and the partial flag in words —
 * the table is where a number gets copied from, so it must not lose the
 * caveat the fading carried.
 */
export function ColumnChartTable({
  series,
  mode = 'stacked',
  partial,
  format = formatCount,
  bucketLabel = (at: string) => at,
  label,
}: {
  series: Series[];
  mode?: 'stacked' | 'grouped';
  partial?: readonly string[] | ReadonlySet<string>;
  format?: (value: number) => string;
  bucketLabel?: (at: string) => string;
  label: string;
}) {
  const buckets = bucketsOf(series);
  const grid = valueGrid(series, buckets);
  const withTotal = mode === 'stacked' && series.length > 1;
  return (
    <div className="table-scroll" tabIndex={0} role="region" aria-label={label}>
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Bucket</th>
            {series.map((entry) => (
              <th scope="col" className="numeric" key={entry.key}>
                {entry.label}
              </th>
            ))}
            {withTotal ? (
              <th scope="col" className="numeric">
                Total
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {/* Newest first: the table is read for "what happened lately". */}
          {[...buckets.keys()].reverse().map((b) => {
            const at = buckets[b]!;
            const values = grid.map((row) => row[b] ?? null);
            const known = values.filter((v): v is number => v !== null);
            return (
              <tr key={at}>
                <td>
                  {bucketLabel(at)}
                  {isPartial(partial, at) ? <span className="muted"> · partial</span> : null}
                </td>
                {values.map((value, s) => (
                  <td className="numeric" key={series[s]!.key}>
                    {value === null ? '—' : format(value)}
                  </td>
                ))}
                {withTotal ? (
                  <td className="numeric">
                    {known.length === 0 ? '—' : format(known.reduce((a, v) => a + v, 0))}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Monday-first, matching ISO weekday order. Pass `rowLabels` for another. */
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * Weekday × hour of day, one hue.
 *
 * The question this answers is "when is this center busy", so it is a
 * ranking of cells by one measure and gets ONE hue whose strength carries the
 * value — the categorical palette would imply the cells differ in kind. The
 * three states are kept apart as in ColumnChart: null is an outlined empty
 * cell (not counted), 0 is a flat surface cell (counted, nothing came), and
 * anything above 0 is tinted, with a floor so a single upload is visible.
 *
 * `values[row][hour]` — `rowLabels.length` rows of 24 hours, in Baghdad time
 * (the caller's subtitle should say so).
 */
export function Heatmap({
  values,
  rowLabels = WEEKDAYS,
  format = formatCount,
  label,
}: {
  values: (number | null)[][];
  rowLabels?: string[];
  format?: (value: number) => string;
  label: string;
}) {
  const [hover, setHover] = useState<{ row: number; hour: number } | null>(null);
  const [hoverX, setHoverX] = useState(0);

  const rows = rowLabels.length;
  const cells = values.slice(0, rows);
  const known = cells.flatMap((row) =>
    row.slice(0, 24).filter((v): v is number => v !== null && Number.isFinite(v)),
  );
  if (known.length === 0) {
    return <p className="viz__empty">Nothing recorded in this window.</p>;
  }
  const max = Math.max(...known, 0);

  const width = 720;
  const left = 40;
  const right = 8;
  const top = 4;
  const cellW = (width - left - right) / 24;
  const cellH = 20;
  const height = top + rows * cellH + 20;

  const hoverValue = hover === null ? undefined : cells[hover.row]?.[hover.hour] ?? null;

  return (
    <div className="viz__plot">
      {hover !== null ? (
        <div
          className="viz__tip"
          style={{
            left: hoverX > 0.6 ? undefined : `${hoverX * 100}%`,
            right: hoverX > 0.6 ? `${(1 - hoverX) * 100}%` : undefined,
          }}
        >
          <div className="viz__tip-when">
            {rowLabels[hover.row]} {hourLabel(hover.hour)}–{hourLabel((hover.hour + 1) % 24)}
          </div>
          <div className="viz__tip-row">
            <span className="viz__tip-value">
              {hoverValue === null || hoverValue === undefined ? '— not counted' : format(hoverValue)}
            </span>
          </div>
        </div>
      ) : null}

      <svg
        className="viz__svg viz__heat"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={label}
        preserveAspectRatio="xMidYMid meet"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const rx = (event.clientX - box.left) / box.width;
          const ry = (event.clientY - box.top) / box.height;
          setHoverX(Math.min(Math.max(rx, 0), 1));
          const hour = Math.floor((rx * width - left) / cellW);
          const row = Math.floor((ry * height - top) / cellH);
          setHover(
            hour >= 0 && hour < 24 && row >= 0 && row < rows ? { row, hour } : null,
          );
        }}
      >
        {rowLabels.map((name, r) => (
          <g key={name}>
            <text className="viz__axis" x={left - 6} y={top + r * cellH + cellH / 2 + 4} textAnchor="end">
              {name}
            </text>
            {Array.from({ length: 24 }, (_, h) => {
              const value = cells[r]?.[h] ?? null;
              const x = left + h * cellW;
              const yy = top + r * cellH;
              const hovered = hover !== null && hover.row === r && hover.hour === h;
              const className =
                value === null
                  ? 'viz__heat-cell viz__heat-cell--empty'
                  : value <= 0
                    ? 'viz__heat-cell viz__heat-cell--zero'
                    : 'viz__heat-cell';
              return (
                <rect
                  key={h}
                  className={hovered ? `${className} viz__heat-cell--hover` : className}
                  x={x + 1}
                  y={yy + 1}
                  width={cellW - 2}
                  height={cellH - 2}
                  rx={2}
                  style={
                    value !== null && value > 0 && max > 0
                      ? { fillOpacity: 0.18 + 0.82 * (value / max) }
                      : undefined
                  }
                />
              );
            })}
          </g>
        ))}
        {Array.from({ length: 8 }, (_, i) => i * 3).map((h) => (
          <text
            key={h}
            className="viz__axis-x"
            x={left + h * cellW + cellW / 2}
            y={height - 4}
            textAnchor="middle"
          >
            {hourLabel(h)}
          </text>
        ))}
      </svg>

      {/* The key. Opacity alone cannot be read as a number, so the scale
          names its ends; the exact counts are in the table toggle. */}
      <div className="viz__heat-scale" aria-hidden="true">
        <span>0</span>
        <span className="viz__heat-ramp" />
        <span>{format(max)}</span>
        <span className="viz__heat-key viz__heat-key--empty" /> not counted
      </div>
    </div>
  );
}

/** The numbers behind a Heatmap, for `ChartFrame`'s table toggle. */
export function HeatmapTable({
  values,
  rowLabels = WEEKDAYS,
  format = formatCount,
  label,
}: {
  values: (number | null)[][];
  rowLabels?: string[];
  format?: (value: number) => string;
  label: string;
}) {
  return (
    <div className="table-scroll" tabIndex={0} role="region" aria-label={label}>
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Day</th>
            {Array.from({ length: 24 }, (_, h) => (
              <th scope="col" className="numeric" key={h}>
                {String(h).padStart(2, '0')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowLabels.map((name, r) => (
            <tr key={name}>
              <th scope="row">{name}</th>
              {Array.from({ length: 24 }, (_, h) => {
                const value = values[r]?.[h] ?? null;
                return (
                  <td className="numeric" key={h}>
                    {value === null ? '—' : format(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
